/* eslint-disable react-refresh/only-export-components */
import * as React from "react"

import * as auth from "@/chat/auth"
import { Connection, resolveSocketUrl } from "@/chat/connection"
import {
  clearToken,
  loadReadState,
  saveReadState,
  storedToken,
  storeToken,
} from "@/chat/identity"
import {
  isVideo,
  type Attachment,
  type Message,
  type ReplySnapshot,
  type RoomSummary,
  type ServerFrame,
  type User,
} from "@/chat/protocol"
import {
  disablePush,
  enablePush,
  forgetPush,
  localNotificationsEnabled,
  readPushState,
  retryPush,
  showLocalNotification,
  syncPush,
  type PushState,
} from "@/chat/push"
import { describeNotification, type PushPayload } from "@/chat/push-format"
import { VoiceCall } from "@/chat/voice"
import {
  initialState,
  reducer,
  toggleReaction,
  type State,
} from "@/chat/reducer"

/**
 * All live chat state, driven by frames from the socket.
 *
 * The reducer is the only place server frames are applied, so the rules that
 * matter — messages are unique by id, ordered by `(at, id)`, and an optimistic
 * row disappears exactly when its broadcast copy lands — are enforced in one
 * place rather than spread across screens.
 *
 * The provider also owns the session: the bearer token, and the account it
 * belongs to. There is no socket without a token, so the socket's lifetime is
 * tied to it.
 */

// --- Context ---------------------------------------------------------------

/**
 * Where the session stands. `checking` is the startup window while a stored
 * token is being validated; nothing is rendered past the gate until it settles.
 */
export type AuthStatus = "checking" | "signedOut" | "signedIn"

export type SignInMode = "login" | "register"

/** How long `openDirect` waits for its answer before giving up. */
const DIRECT_TIMEOUT_MS = 10_000

const SESSION_EXPIRED = "Сессия истекла. Войдите снова."
const SIGNED_OUT_ELSEWHERE = "Вы вышли из аккаунта на другом устройстве."

type ChatValue = State & {
  authStatus: AuthStatus
  /** The signed-in account; null until the session is established. */
  user: User | null
  /** Why the last session ended, for the sign-in screen. Null after a plain sign-out. */
  sessionNotice: string | null
  /** Resolves on success, rejects with an `AuthError` on failure. */
  signIn: (mode: SignInMode, login: string, password: string) => Promise<void>
  signOut: () => void
  openRoom: (roomId: string) => void
  /** `before` is the id of the oldest message currently on screen. */
  loadOlder: (roomId: string, before: string) => void
  /**
   * `attachments` are already uploaded via `uploadMedia`. `replyTo` is the
   * message this one answers; the snapshot is what the optimistic row quotes
   * until the server's copy, which carries its own, replaces it.
   */
  send: (
    roomId: string,
    text: string,
    attachments?: Attachment[],
    replyTo?: ReplySnapshot | null
  ) => void
  /** Uploads a picture or video for a later `send`. Rejects with an `AuthError`. */
  uploadMedia: (file: Blob, options?: auth.UploadOptions) => Promise<Attachment>
  notifyTyping: (roomId: string) => void
  /**
   * Toggles your `emoji` on a message. Applied optimistically; the server's
   * `reactions` frame then settles the list for everyone.
   */
  react: (roomId: string, messageId: string, emoji: string) => void
  /** Creates a group with the caller and `members`; it arrives as `roomCreated`. */
  createRoom: (name: string, members?: string[], topic?: string) => void
  /** Adds a user to a group the caller is in. */
  invite: (roomId: string, userId: string) => void
  /** Leaves a group; the room arrives back as `roomRemoved`. */
  leave: (roomId: string) => void
  /** The direct room with `userId`, created if needed. Rejects on error. */
  openDirect: (userId: string) => Promise<RoomSummary>
  /** Accounts whose login starts with `q`, the caller excluded. */
  searchUsers: (q: string) => Promise<User[]>
  /** `at` is the timestamp of the newest message the reader has seen. */
  markRead: (roomId: string, at: number) => void
  unreadRooms: Record<string, boolean>
  retry: () => void
  dismissNotice: () => void
  /** Where the notifications switch stands for this browser. */
  pushState: PushState
  /**
   * Turns notifications on or off and resolves with the state that resulted
   * — `local` when Web Push could not be set up. Call from a click: turning it on prompts for
   * permission. Rejects with a user-facing `Error` when it cannot.
   */
  setPushEnabled: (enabled: boolean) => Promise<PushState>
  /** The voice chat this browser is in, or null. */
  call: CallState | null
  /**
   * Enters `roomId`'s voice chat, leaving any other. Call from a click: it
   * asks for the microphone. Rejects with a `MicrophoneError`.
   */
  joinVoice: (roomId: string) => Promise<void>
  leaveVoice: () => void
  setVoiceMuted: (muted: boolean) => void
}

/** The voice chat this browser is in, as the UI needs it. */
export type CallState = {
  roomId: string
  /** Null until the server has answered the join. */
  peerId: string | null
  muted: boolean
  /** Peer ids, and `SELF`, of whoever is talking right now. */
  speaking: string[]
  connections: Record<string, RTCPeerConnectionState>
}

const ChatContext = React.createContext<ChatValue | undefined>(undefined)

type Session = { token: string; user: User }

type DirectWaiter = {
  resolve: (room: RoomSummary) => void
  reject: (error: Error) => void
}

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(reducer, initialState)
  const [session, setSession] = React.useState<Session | null>(null)
  const [authStatus, setAuthStatus] = React.useState<AuthStatus>(() =>
    storedToken() ? "checking" : "signedOut"
  )
  const [sessionNotice, setSessionNotice] = React.useState<string | null>(null)
  const [readState, setReadState] = React.useState(loadReadState)

  const connectionRef = React.useRef<Connection | null>(null)

  // The latest state for callbacks that must stay stable across renders.
  // Written in an effect, as the hooks lint asks; a click always comes after
  // the commit that made the state it reads.
  const stateRef = React.useRef(state)
  React.useEffect(() => {
    stateRef.current = state
  }, [state])

  /**
   * Latest values the handshake needs, read at reconnect time. A ref keeps the
   * socket effect from tearing down every time the open room changes.
   */
  const sessionRef = React.useRef<{
    token: string | null
    roomId: string | null
  }>({ token: null, roomId: null })

  /** Callers of `openDirect` waiting for the server's `direct` frame. */
  const directWaiters = React.useRef<Set<DirectWaiter>>(new Set())

  /**
   * The call engine lives outside React state: it is fed frames straight
   * from the socket, and `call` is only a snapshot of it for rendering.
   */
  const callRef = React.useRef<{
    roomId: string
    peerId: string | null
    engine: VoiceCall
  } | null>(null)
  const [call, setCall] = React.useState<CallState | null>(null)

  const refreshCall = React.useCallback(() => {
    const current = callRef.current
    setCall(
      current && {
        roomId: current.roomId,
        peerId: current.peerId,
        muted: current.engine.muted,
        speaking: [...current.engine.speaking],
        connections: current.engine.connectionStates(),
      }
    )
  }, [])

  const endCall = React.useCallback(() => {
    const current = callRef.current
    if (!current) return
    callRef.current = null
    current.engine.close()
    refreshCall()
  }, [refreshCall])

  const token = session?.token ?? null

  const [pushState, setPushState] = React.useState<PushState>("unsupported")

  // Whatever the browser already holds is re-registered under this account
  // as soon as there is one, so a second sign-in on the device does not have
  // to find the switch again.
  React.useEffect(() => {
    let cancelled = false

    const settle = () =>
      readPushState().then((next) => {
        if (!cancelled) setPushState(next)
      })

    // A browser left in the `local` state gets another try at Web Push on
    // every start: the network that blocked the push service may not now.
    if (token) {
      syncPush(token)
        .then(readPushState)
        .then((state) => (state === "local" ? retryPush(token) : false))
        .finally(() => void settle())
    } else void settle()

    // The worker says the push service rotated the endpoint.
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === "pushSubscriptionChanged" && token) {
        syncPush(token).finally(() => void settle())
      }
    }
    navigator.serviceWorker?.addEventListener("message", onMessage)

    return () => {
      cancelled = true
      navigator.serviceWorker?.removeEventListener("message", onMessage)
    }
  }, [token])

  const setPushEnabled = React.useCallback(
    async (enabled: boolean): Promise<PushState> => {
      const current = sessionRef.current.token
      if (!current) return readPushState()

      setPushState("busy")
      try {
        if (enabled) await enablePush(current)
        else await disablePush(current)
      } finally {
        setPushState(await readPushState())
      }
      return readPushState()
    },
    []
  )

  const endSession = React.useCallback((reason: string | null = null) => {
    clearToken()
    setSession(null)
    setSessionNotice(reason)
    setAuthStatus("signedOut")
    sessionRef.current.token = null
    sessionRef.current.roomId = null
  }, [])

  /**
   * Startup: validate a stored token before opening a socket. A dead token
   * goes straight to the sign-in screen; a network failure is not the token's
   * fault, so the socket is opened anyway and `hello` settles the question.
   */
  React.useEffect(() => {
    const stored = storedToken()
    if (!stored) return

    let cancelled = false

    auth
      .me(stored)
      .then((user) => {
        if (cancelled) return
        setSession({ token: stored, user })
        setAuthStatus("signedIn")
      })
      .catch((error: unknown) => {
        if (cancelled) return

        if (error instanceof auth.AuthError && error.code === "unauthorized") {
          endSession(error.message)
          return
        }

        // Offline or a degraded server: proceed with the token and let the
        // socket's reconnect loop and `hello` sort it out. `user` fills in
        // from `welcome`.
        setSession({ token: stored, user: { id: "", name: "" } })
        setAuthStatus("signedIn")
      })

    return () => {
      cancelled = true
    }
  }, [endSession])

  React.useEffect(() => {
    if (!token) return

    sessionRef.current.token = token

    const connection = new Connection(resolveSocketUrl())
    connectionRef.current = connection

    // Re-sent on every reconnect, so a dropped socket restores the session and
    // rejoins whichever room is on screen without the UI noticing.
    connection.handshake = () => {
      const { token: current, roomId } = sessionRef.current
      if (!current) return []

      const frames = [{ type: "hello" as const, token: current }]
      return roomId ? [...frames, { type: "open" as const, roomId }] : frames
    }

    const waiters = directWaiters.current

    const settleDirect = (frame: ServerFrame) => {
      if (waiters.size === 0) return

      if (frame.type === "direct") {
        for (const waiter of waiters) waiter.resolve(frame.room)
        waiters.clear()
      } else if (frame.type === "error") {
        for (const waiter of waiters) waiter.reject(new Error(frame.message))
        waiters.clear()
      }
    }

    /**
     * The fallback for a device its push service cannot reach: every
     * message of every room arrives here anyway, so while the app is open
     * but not in front of the person, it notifies by itself. The rule for
     * "in front" is the worker's, so the two never disagree: a visible,
     * focused window needs no notification.
     */
    const notifyLocally = (
      roomId: string,
      author: User,
      content: Pick<PushPayload, "text" | "attachments" | "videos" | "voice">
    ) => {
      const { self, rooms } = stateRef.current
      if (!self || author.id === self.id) return
      if (document.visibilityState === "visible" && document.hasFocus()) return
      if (!localNotificationsEnabled()) return

      const room = rooms.find((r) => r.id === roomId)
      if (!room) return

      const { title, body } = describeNotification({
        roomId: room.id,
        roomKind: room.kind,
        roomName: room.name,
        author,
        at: Date.now(),
        ...content,
      })
      void showLocalNotification(room.id, title, body)
    }

    const notifyMessage = (message: Message) => {
      if (message.nonce) return
      notifyLocally(
        message.roomId,
        { id: message.userId, name: message.name },
        {
          // Cut as the server cuts a push's text, so both read the same.
          text: message.text.slice(0, 200),
          attachments: message.attachments.length,
          videos: message.attachments.filter(isVideo).length,
        }
      )
    }

    /** Feeds the call engine whatever of `frame` concerns the call. */
    const applyVoice = (frame: ServerFrame) => {
      const current = callRef.current

      if (frame.type === "voiceState" && frame.startedBy) {
        notifyLocally(frame.roomId, frame.startedBy, {
          text: "",
          attachments: 0,
          videos: 0,
          voice: true,
        })
      }

      if (!current) return

      switch (frame.type) {
        case "welcome":
          // A new socket is a new session on the server, which has already
          // dropped the old one from the chat: join again, afresh. A call
          // still waiting on the microphone joins once it has it.
          if (!current.engine.ready) return
          current.engine.resetPeers()
          current.peerId = null
          connection.sendEphemeral({
            type: "voiceJoin",
            roomId: current.roomId,
          })
          refreshCall()
          break

        case "voiceJoined":
          if (frame.roomId !== current.roomId) return
          current.peerId = frame.peerId
          current.engine.configure(frame.iceServers)
          current.engine.connectTo(frame.peers.map((p) => p.peerId))
          if (current.engine.muted) {
            connection.sendEphemeral({
              type: "voiceMute",
              roomId: current.roomId,
              muted: true,
            })
          }
          refreshCall()
          break

        case "voiceSignal":
          if (frame.roomId === current.roomId) {
            current.engine.handleSignal(frame.from, frame.signal)
          }
          break

        case "voiceState":
          if (frame.roomId !== current.roomId || !current.peerId) return
          current.engine.retain(
            new Set(
              frame.peers
                .map((p) => p.peerId)
                .filter((id) => id !== current.peerId)
            )
          )
          break

        case "voiceLeft":
        case "roomRemoved":
          if (frame.roomId === current.roomId) endCall()
          break

        case "error":
          if (frame.code === "voice_full") endCall()
          break
      }
    }

    connection.listen({
      onFrame: (frame) => {
        dispatch({ type: "frame", frame })
        settleDirect(frame)
        if (frame.type === "message") notifyMessage(frame.message)
        applyVoice(frame)

        // `welcome` is the authoritative answer to who the token belongs to;
        // the session remembers it so a reconnect's reset does not blank it.
        if (frame.type === "welcome") {
          const { self } = frame
          setSession((prev) =>
            prev && prev.user.id !== self.id ? { ...prev, user: self } : prev
          )
        }

        // The server refused the token over the socket: the session is over.
        if (frame.type === "error" && frame.code === "unauthorized") {
          endSession(frame.message || SESSION_EXPIRED)
        }
      },
      onStatus: (status) => dispatch({ type: "status", status }),
      onLoggedOut: () => endSession(SIGNED_OUT_ELSEWHERE),
    })

    connection.start()

    return () => {
      endCall()
      connection.dispose()
      connectionRef.current = null
      for (const waiter of waiters)
        waiter.reject(new Error("Соединение разорвано."))
      waiters.clear()
      dispatch({ type: "reset" })
    }
  }, [token, endSession, endCall, refreshCall])

  // Expire typing notices. Runs only while someone is actually typing.
  const hasTyping = Object.values(state.typing).some((e) => e.length > 0)
  React.useEffect(() => {
    if (!hasTyping) return

    const timer = setInterval(
      () => dispatch({ type: "sweepTyping", now: Date.now() }),
      1000
    )
    return () => clearInterval(timer)
  }, [hasTyping])

  const signIn = React.useCallback(
    async (mode: SignInMode, login: string, password: string) => {
      const result =
        mode === "register"
          ? await auth.register(login, password)
          : await auth.login(login, password)

      storeToken(result.token)
      setSession(result)
      setSessionNotice(null)
      setAuthStatus("signedIn")
    },
    []
  )

  const signOut = React.useCallback(() => {
    const current = sessionRef.current.token
    endSession()

    // Revoking is global for the token, so it happens after the local state is
    // gone: the server will close our own socket with 4001, which the
    // connection is already disposed for. The push subscription is dropped
    // server-side first, while the token still works — a signed-out device
    // must not keep receiving this account's messages.
    if (current) {
      forgetPush(current)
        .then(() => auth.logout(current))
        .catch(() => {
          // The token is forgotten locally either way; it expires on its own.
        })
    }
  }, [endSession])

  const openRoom = React.useCallback((roomId: string) => {
    sessionRef.current.roomId = roomId
    dispatch({ type: "loading", roomId })
    connectionRef.current?.send({ type: "open", roomId })
  }, [])

  const loadOlder = React.useCallback((roomId: string, before: string) => {
    if (!before) return

    dispatch({ type: "loading", roomId })
    connectionRef.current?.send({ type: "open", roomId, before })
  }, [])

  const send = React.useCallback(
    (
      roomId: string,
      text: string,
      attachments: Attachment[] = [],
      replyTo: ReplySnapshot | null = null
    ) => {
      const body = text.trim()
      if (!body && attachments.length === 0) return

      const nonce = crypto.randomUUID()

      // Rendered immediately as pending; the server's echo carries this nonce
      // and retires the placeholder, so the message never appears twice.
      dispatch({
        type: "queued",
        roomId,
        message: { nonce, text: body, attachments, replyTo, at: Date.now() },
      })

      connectionRef.current?.send({
        type: "send",
        roomId,
        text: body,
        attachments: attachments.map((a) => a.id),
        nonce,
        replyTo: replyTo?.id ?? null,
      })
    },
    []
  )

  const uploadMedia = React.useCallback(
    (file: Blob, options?: auth.UploadOptions) => {
      const current = sessionRef.current.token
      if (!current) {
        return Promise.reject(
          new auth.AuthError("unauthorized", SESSION_EXPIRED)
        )
      }
      return auth.uploadMedia(current, file, options)
    },
    []
  )

  const notifyTyping = React.useCallback((roomId: string) => {
    // Dropped rather than queued when offline — a stale notice is only noise.
    connectionRef.current?.sendEphemeral({ type: "typing", roomId })
  }, [])

  const react = React.useCallback(
    (roomId: string, messageId: string, emoji: string) => {
      const self = stateRef.current.self
      const message = stateRef.current.messages[roomId]?.find(
        (m) => m.id === messageId
      )
      if (!self || !message) return

      const mine = message.reactions.some(
        (r) => r.emoji === emoji && r.userIds.includes(self.id)
      )

      // Show the change now: the frame back takes a round trip, and a tap that
      // does nothing for that long reads as a miss.
      dispatch({
        type: "frame",
        frame: {
          type: "reactions",
          roomId,
          messageId,
          reactions: toggleReaction(message.reactions, emoji, self.id, !mine),
        },
      })

      connectionRef.current?.send({
        type: "react",
        roomId,
        messageId,
        emoji,
        remove: mine || undefined,
      })
    },
    []
  )

  const createRoom = React.useCallback(
    (roomName: string, members: string[] = [], topic?: string) => {
      connectionRef.current?.send({
        type: "createRoom",
        name: roomName,
        topic,
        members,
      })
    },
    []
  )

  const invite = React.useCallback((roomId: string, userId: string) => {
    connectionRef.current?.send({ type: "invite", roomId, userId })
  }, [])

  const leave = React.useCallback((roomId: string) => {
    if (sessionRef.current.roomId === roomId) sessionRef.current.roomId = null
    connectionRef.current?.send({ type: "leave", roomId })
  }, [])

  const openDirect = React.useCallback((userId: string) => {
    return new Promise<RoomSummary>((resolve, reject) => {
      const connection = connectionRef.current
      if (!connection) {
        reject(new Error("Нет соединения."))
        return
      }

      const waiters = directWaiters.current
      const timer = setTimeout(() => {
        waiters.delete(waiter)
        reject(new Error("Сервер не ответил."))
      }, DIRECT_TIMEOUT_MS)

      const waiter: DirectWaiter = {
        resolve: (room) => {
          clearTimeout(timer)
          resolve(room)
        },
        reject: (error) => {
          clearTimeout(timer)
          reject(error)
        },
      }

      waiters.add(waiter)
      connection.send({ type: "openDirect", userId })
    })
  }, [])

  const searchUsers = React.useCallback((q: string) => {
    const current = sessionRef.current.token
    if (!current) return Promise.resolve([])
    return auth.searchUsers(current, q)
  }, [])

  const markRead = React.useCallback((roomId: string, at: number) => {
    setReadState((prev) => {
      if ((prev[roomId] ?? 0) >= at) return prev

      const next = { ...prev, [roomId]: at }
      saveReadState(next)
      return next
    })
  }, [])

  const retry = React.useCallback(() => {
    connectionRef.current?.reconnectNow()
  }, [])

  const dismissNotice = React.useCallback(
    () => dispatch({ type: "dismissNotice" }),
    []
  )

  /**
   * A room is unread when its newest message is newer than the last time this
   * device opened it. The server keeps no read state yet, so this cannot
   * follow a person between devices.
   */
  const unreadRooms = React.useMemo(() => {
    const unread: Record<string, boolean> = {}

    for (const room of state.rooms) {
      const last = room.lastMessage
      unread[room.id] =
        last !== null &&
        last.userId !== state.self?.id &&
        last.at > (readState[room.id] ?? 0)
    }

    return unread
  }, [state.rooms, state.self, readState])

  // The installed app's icon badge counts unread rooms. Best effort: the
  // API is missing in most browsers and may refuse without a permission.
  React.useEffect(() => {
    if (!("setAppBadge" in navigator)) return
    const count = Object.values(unreadRooms).filter(Boolean).length
    const update =
      count > 0 ? navigator.setAppBadge(count) : navigator.clearAppBadge()
    update.catch(() => {})
  }, [unreadRooms])

  const leaveVoice = React.useCallback(() => {
    const current = callRef.current
    if (!current) return
    connectionRef.current?.sendEphemeral({
      type: "voiceLeave",
      roomId: current.roomId,
    })
    endCall()
  }, [endCall])

  const joinVoice = React.useCallback(
    async (roomId: string) => {
      if (callRef.current?.roomId === roomId) return
      leaveVoice()

      const engine = new VoiceCall({
        signal: (to, signal) =>
          connectionRef.current?.sendEphemeral({
            type: "voiceSignal",
            roomId,
            to,
            signal,
          }),
        change: refreshCall,
      })
      callRef.current = { roomId, peerId: null, engine }
      refreshCall()

      try {
        await engine.start()
      } catch (error) {
        if (callRef.current?.engine === engine) endCall()
        else engine.close()
        throw error
      }

      // Hung up, or moved to another room, while the microphone prompt was up.
      if (callRef.current?.engine !== engine) return

      // Dropped if the socket is down; the `welcome` that follows the
      // reconnect joins instead.
      connectionRef.current?.sendEphemeral({ type: "voiceJoin", roomId })
    },
    [leaveVoice, endCall, refreshCall]
  )

  const setVoiceMuted = React.useCallback(
    (muted: boolean) => {
      const current = callRef.current
      if (!current) return
      current.engine.setMuted(muted)
      connectionRef.current?.sendEphemeral({
        type: "voiceMute",
        roomId: current.roomId,
        muted,
      })
      refreshCall()
    },
    [refreshCall]
  )

  const user = state.self ?? (session?.user.id ? session.user : null)

  const value = React.useMemo<ChatValue>(
    () => ({
      ...state,
      authStatus,
      user,
      sessionNotice,
      signIn,
      signOut,
      openRoom,
      loadOlder,
      send,
      uploadMedia,
      notifyTyping,
      react,
      createRoom,
      invite,
      leave,
      openDirect,
      searchUsers,
      markRead,
      unreadRooms,
      retry,
      dismissNotice,
      pushState,
      setPushEnabled,
      call,
      joinVoice,
      leaveVoice,
      setVoiceMuted,
    }),
    [
      state,
      authStatus,
      user,
      sessionNotice,
      signIn,
      signOut,
      openRoom,
      loadOlder,
      send,
      uploadMedia,
      notifyTyping,
      react,
      createRoom,
      invite,
      leave,
      openDirect,
      searchUsers,
      markRead,
      unreadRooms,
      retry,
      dismissNotice,
      pushState,
      setPushEnabled,
      call,
      joinVoice,
      leaveVoice,
      setVoiceMuted,
    ]
  )

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

export function useChat(): ChatValue {
  const context = React.useContext(ChatContext)

  if (context === undefined) {
    throw new Error("useChat must be used within a ChatProvider")
  }

  return context
}

/**
 * Names currently shown as typing. Expiry is handled by the provider's sweep
 * rather than filtered here, so this stays a pure read of state.
 */
export function useTypingNames(roomId: string): string[] {
  const { typing } = useChat()

  return (typing[roomId] ?? []).map((entry) => entry.user.name)
}
